import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// Single source of truth for route-aware SEO metadata (Phase 1).
// Canonical is derived from the CLEAN pathname (query/UTM stripped) unless an
// explicit canonical is supplied — so campaign/UTM/filter params never create
// competing canonical identities.

const SITE = "https://adaptience.com";
const DEFAULT_OG_IMAGE = `${SITE}/og/home-1200x630.jpg`;

export default function Seo({
  title,
  description,
  canonical,
  robots = "index, follow, max-image-preview:large",
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  ogUrl,
  twitterCard = "summary_large_image",
  jsonLd,
}) {
  const location = useLocation();

  let canonicalUrl;
  if (canonical) {
    canonicalUrl = canonical.startsWith("http") ? canonical : `${SITE}${canonical}`;
  } else {
    const cleanPath = location.pathname.replace(/\/+$/, "");
    canonicalUrl = cleanPath ? `${SITE}${cleanPath}` : `${SITE}/`;
  }

  // og:url may differ from canonical (e.g. Preview shares the clean preview URL
  // while canonicalising to the permanent simulation).
  let ogUrlFinal = canonicalUrl;
  if (ogUrl) ogUrlFinal = ogUrl.startsWith("http") ? ogUrl : `${SITE}${ogUrl}`;

  const finalOgTitle = ogTitle || title;
  const finalOgDesc = ogDescription || description;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Adaptience™" />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDesc} />
      <meta property="og:url" content={ogUrlFinal} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDesc} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {Array.isArray(jsonLd)
        ? jsonLd.map((obj, i) => (
            <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
          ))
        : null}
    </Helmet>
  );
}
